import React from 'react';

import { useLanguage } from '../contexts/LanguageContext';
import { Users, Target, Award, Globe, Calendar, MapPin } from 'lucide-react';

const About = () => {

    const { t } = useLanguage();

  const objectives = [
    {
      icon: Users,
      title: 'Développer les talents locaux',
      description: 'Former et accompagner la nouvelle génération de surfeurs africains vers l\'excellence.'
    },
    {
      icon: Award,
      title: 'Organiser des compétitions',
      description: 'Créer des événements de niveau international pour promouvoir le surf africain.'
    },
    {
      icon: Globe,
      title: 'Représentation mondiale',
      description: 'Porter la voix de l\'Afrique dans les instances internationales comme l\'ISA et les JO.'
    },
    {
      icon: Target,
      title: 'Levier économique',
      description: 'Utiliser le surf comme moteur de développement économique et écologique.'
    }
  ];

  const timeline = [
    {
      year: '2017',
      title: 'Fondation à Rabat',
      description: 'Création de l\'ASCsurf avec la vision d\'unir l\'Afrique autour du surf.'
    },
    {
      year: '2018',
      title: 'Premiers championnats',
      description: 'Lancement des premières compétitions continentales officielles.'
    },
    {
      year: '2020',
      title: '10 pays membres',
      description: 'Expansion rapide avec l\'adhésion de 10 fédérations nationales.'
    },
    {
      year: '2022',
      title: 'Africa Surf Tour',
      description: 'Création du circuit professionnel africain de surf.'
    },
    {
      year: '2024',
      title: '15 pays membres',
      description: 'Consolidation avec 15 fédérations et plus de 300 athlètes.'
    },
    {
      year: '2025',
      title: 'Championnats d\'Afrique',
      description: 'Championnat d\'Afrique de surf en Angola avec la FADEN.'
    }
  ];

  return (
    <div className="">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {t('about.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
               {t('about.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('about.vision.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('about.vision.p1')}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t('about.vision.p2')}
              </p>
              <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-400">
                <p className="text-orange-800 font-medium italic">
                   {t('about.vision.quote')}
                </p>
                <p className="text-orange-600 mt-2 font-semibold"> {t('about.vision.author')}</p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://i.imgur.com/uRoSTuy.jpeg?auto=compress&cs=tinysrgb&w=800" 
                alt="Surfeurs africains"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg">
                <div className="flex items-center space-x-3">
                  <Calendar className="h-6 w-6 text-blue-600" />
                  <div>
                    <div className="text-sm text-gray-600">{t('about.vision.founded')}</div>
                    <div className="font-bold text-gray-900">2017</div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 bg-white p-4 rounded-lg shadow-lg">
                <div className="flex items-center space-x-3">
                  <MapPin className="h-6 w-6 text-orange-600" />
                  <div>
                    <div className="text-sm text-gray-600">{t('about.vision.based')}</div>
                    <div className="font-bold text-gray-900">Rabat</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Objectives */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
             {t('about.objectives.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.objectives.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
    <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="flex items-start space-x-4">
            <div className="bg-gradient-to-br from-blue-500 to-teal-500 p-3 rounded-lg"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-users h-6 w-6 text-white"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg></div>
            <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('about.objectives.one.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('about.objectives.one.subtitle')}</p>
            </div>
        </div>
    </div>
    <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="flex items-start space-x-4">
            <div className="bg-gradient-to-br from-blue-500 to-teal-500 p-3 rounded-lg"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-award h-6 w-6 text-white"><circle cx="12" cy="8" r="6"></circle><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"></path></svg></div>
            <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('about.objectives.two.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('about.objectives.two.subtitle')}</p>
            </div>
        </div>
    </div>
    <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="flex items-start space-x-4">
            <div className="bg-gradient-to-br from-blue-500 to-teal-500 p-3 rounded-lg"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-globe h-6 w-6 text-white"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path><path d="M2 12h20"></path></svg></div>
            <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('about.objectives.three.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('about.objectives.three.subtitle')}</p>
            </div>
        </div>
    </div>
    <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="flex items-start space-x-4">
            <div className="bg-gradient-to-br from-blue-500 to-teal-500 p-3 rounded-lg"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-target h-6 w-6 text-white"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg></div>
            <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('about.objectives.for.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('about.objectives.for.subtitle')}</p>
            </div>
        </div>
    </div>
</div>
</div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('about.history.title')}
            </h2>
            <p className="text-xl text-gray-600">
            {t('about.history.subtitle')}
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-500 to-teal-500 rounded-full"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}>
                    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                      <div className="text-2xl font-bold text-blue-600 mb-2">{item.year}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                  <div className="relative">
                    <div className="w-4 h-4 bg-orange-400 rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section Rapports & Documents officiels */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('about.documents.title') || 'Documents & Rapports officiels'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.documents.subtitle') || 'Consultez et téléchargez les documents clés de l\'African Surf Confederation'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Rapport 1 */}
            <a
              href="/documents/ASC_New Members Exc Commissions_Proposal_EN.pdf"
              download="ASC_New_Members_Exc_Commissions_Proposal_EN.pdf"
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 flex items-center space-x-6 border border-gray-200 hover:border-blue-500"
            >
              <div className="bg-red-100 p-4 rounded-xl group-hover:bg-red-200 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  New Members & Executive Commissions Proposal
                </h3>
                <p className="text-gray-600">Proposition officielle pour les nouveaux membres et commissions exécutives (EN)</p>
                <span className="inline-flex items-center mt-3 text-blue-600 font-medium">
                  Télécharger le PDF 
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </span>
              </div>
            </a>

            {/* Rapport 2 */}
            <a
              href="/documents/Report President _African_Surf_Confederation.pdf"
              download="Report_President_African_Surf_Confederation.pdf"
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 flex items-center space-x-6 border border-gray-200 hover:border-orange-500"
            >
              <div className="bg-orange-100 p-4 rounded-xl group-hover:bg-orange-200 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                  Rapport du Président
                </h3>
                <p className="text-gray-600">Rapport officiel du Président de l’African Surf Confederation</p>
                <span className="inline-flex items-center mt-3 text-orange-600 font-medium">
                  Télécharger le PDF 
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default About;